import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getCustomerSessionDetails } from "../services/customerSessionDetails";
import {
	Card,
	CardContent,
	Typography,
	Grid,
	CircularProgress,
	IconButton,
	Box,
	Tooltip,
	Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { styled } from '@mui/material/styles';
import { getEffectiveAccountId } from '../utils/accountId';

const FormContainer = styled(Card)({
	background: 'white',
	borderRadius: '20px',
	padding: '30px',
	boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
	marginBottom: '20px',
});

const ViewPin = () => {
	const location = useLocation();
	// Accounts 4/5 behave like accounts 2/3.
	const accountId = getEffectiveAccountId(localStorage.getItem("account_id"));

	const { sessionId, name, preferredLanguage, treatmentArm, pin } = location.state || {};
	const navigate = useNavigate();
	const [sessionDetails, setSessionDetails] = useState(null);
	const [loading, setLoading] = useState(true);

	// const pin = "1234"; // Hardcoded PIN
	const [tooltipText, setTooltipText] = useState("Copy PIN");

	useEffect(() => {
		const fetchSessionDetails = async () => {
			try {
				if (sessionId) {
					const data = await getCustomerSessionDetails(sessionId);
					console.log("data", data)
					setSessionDetails(data);
				}
			} catch (error) {
				console.error("Failed to fetch session details:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchSessionDetails();
	}, [sessionId]);

	const [copied, setCopied] = useState(false);

	const handleCopyPin = () => {
		if (navigator.clipboard && window.isSecureContext) {
			navigator.clipboard.writeText(pin);
		} else {
			const textArea = document.createElement("textarea");
			textArea.value = pin;
			textArea.style.position = "fixed";
			textArea.style.left = "-9999px";
			document.body.appendChild(textArea);
			textArea.select();
			document.execCommand("copy");
			document.body.removeChild(textArea);
		}
		setTooltipText("Copied!");
		setCopied(true);
		setTimeout(() => {
			setTooltipText("Copy PIN");
			setCopied(false);
		}, 2000);
	};

	if (loading) return <CircularProgress />;
	if (!sessionDetails) return <Typography>No session details found.</Typography>;

	

	const sleepQualityMap = {
		very_poor: "😫 Very Poor",
		poor: "😴 Poor",
		fair: "😌 Fair",
		good: "😊 Good",
		excellent: "🌟 Excellent",
	};

	const assessmentTypeMap = {
		hads: "HADS Assessment",
		factg7: "FACT-G7 Assessment",
		eortc: "EORTC QLQ-C30",
	};

	const moodRatingMap = {
		happy: "😊 Happy",
		sad: "😞 Sad",
		angry: "😡 Angry",
		disgust: "🤢 Disgust",
		surprised: "😲 Surprised",
		bad_low: "😔 Bad / Low",
		none: "❓ None of the above",
		// very_poor: "😫 Very Poor",
		// poor: "😴 Poor",
		// fair: "😌 Fair",
		// good: "😊 Good",
		// excellent: "🌟 Excellent",
	};


	const assessments =
		accountId === "1"
			? [
				{
					title: "HADS Assessment",
					subtitle: "Hospital Anxiety and Depression Scale",
					key: "hads",
				},
				// {
				//     title: "FACT-G7 Assessment",
				//     subtitle: "Functional Assessment of Cancer Therapy",
				//     key: "factg7",
				// },
				{
					title: "EORTC QLQ-C30",
					subtitle: "European Quality of Life Questionnaire",
					key: "eortc",
				},
			]
			: [
				{
					title: "HADS Assessment",
					subtitle: "Hospital Anxiety and Depression Scale",
					key: "hads",
				},
				{
					title: "PSQI Assessment",
					subtitle: "Sleep Quality measurement",
					key: "psqi",
				},
				{
					title: "EORTC QLQ-C30",
					subtitle: "European Quality of Life Questionnaire",
					key: "eortc",
				},
			];

	console.log("sessionDetails.package_type", sessionDetails.package_type)
	const fields = [
		{ label: "Treatment Cycle", value: sessionDetails.treatment_cycle },
		{ label: "Blood Pressure", value: sessionDetails.blood_pressure ? `${sessionDetails.blood_pressure} mmHg` : "-" },
		{ label: "Heart Rate", value: sessionDetails.heart_rate ? `${sessionDetails.heart_rate} bpm` : "-" },
		{ label: "Pain Scale", value: sessionDetails.pain_scale ? `${sessionDetails.pain_scale}/10` : "-" },
		{
			label: "Mood Rating",
			value: moodRatingMap[sessionDetails.mood_rating] || "-",
		},
		{ label: "Nausea", value: sessionDetails.nausea || "-" },
		{ label: "Fatigue", value: sessionDetails.fatigue || "-" },
		{ label: "Anxiety", value: sessionDetails.anxiety || "-" },
		{ label: "Depression", value: sessionDetails.depression || "-" },
		{ label: "Appetite Loss", value: sessionDetails.appetite_loss || "-" },
		{
			label: "Sleep Quality",
			value: sleepQualityMap[sessionDetails.sleep_quality] || "-",
		},
		{
			label: "Assessment Type",
			value: sessionDetails.assessment_type
				? sessionDetails.assessment_type
					.map((item) => {
						const match = assessments.find(a => a.key === item.title);
						return match ? match.title : item.title;
					})
					.join(", ")
				: "-"
		},
		{
			label: preferredLanguage ? "Suggested Care Soundscape" : "Suggested Care Soundscape",
			value: sessionDetails.track_name || "-",
		},
	];

	const languageMap = {
		eng: "English",
		hin: "Hindi",
		tel: "Telugu",
		ben: "Bengali",
		tam: "Tamil",
		 kan: "Kannada",
	};

	// Get full language name or fallback to the code
	const displayLanguage = languageMap[preferredLanguage] || preferredLanguage || "-";

	return (
		<FormContainer>
			{/* Header Box */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2
				}}
			>
				<Typography
					variant="h6"
					fontWeight={400}
					sx={{ color: "#1f2937" }}
				>
					View Pin
				</Typography>

				<Button
					variant="contained"
					onClick={() => navigate("/patients")}
					sx={{
						bgcolor: "#4A90E2",
						"&:hover": { bgcolor: "#357ABD" },
						borderRadius: { xs: 3, sm: 2 },
						px: { xs: 2, md: 2.5 },
						py: { xs: 1, sm: 1 },
						textTransform: "none",
						fontWeight: 600,
						fontSize: { xs: "0.8rem", sm: "0.875rem" },
						boxShadow: {
							xs: "0 4px 12px rgba(74, 144, 226, 0.3)",
							sm: "0 1px 3px rgba(0, 0, 0, 0.1)",
						},
						whiteSpace: "nowrap",
						minWidth: { xs: "auto", sm: "auto" },
						height: { xs: 40, sm: "auto" },
					}}
				>
					Back
				</Button>
			</Box>

			{/* PIN Card */}
			<Card sx={{ marginBottom: 2, textAlign: "center", padding: 2, background: '#f8f9ff' }}>
				<CardContent>
					<Typography
						variant="h6"
						fontWeight={400}
						sx={{ color: "#1a1238", }}
					>
						Patient Portal Access PIN
					</Typography>
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: 2,
							marginTop: 1,
						}}
					>
						<Typography variant="h4" sx={{ letterSpacing: 4 }}>
							{pin}
						</Typography>
						<Tooltip title={tooltipText} arrow>
							<IconButton
								onClick={handleCopyPin}
								color="primary"
								disableRipple
								sx={{
									"&:focus": {
										outline: "none",
										boxShadow: "none",
									},
								}}
							>
								{copied ? <AssignmentTurnedInIcon /> : <ContentCopyIcon />}
							</IconButton>
						</Tooltip>
					</Box>
					<Typography variant="body2" color="textSecondary">
						Share this PIN with the {name} to access their portal.
					</Typography>
				</CardContent>
			</Card>

			{/* Session Details Card */}
			<Card sx={{ padding: 2, background: '#f8f9ff' }}>
				<CardContent>
					<Typography
						variant="h6"
						fontWeight={400}
						sx={{ color: "#1a1238", mb: 3, textAlign: "flex-start" }}
					>
						Session Details
					</Typography>

					<Box
    sx={{
        display: "grid",
        gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
        },
        gap: 2,
        alignItems: "start",
    }}
>
    {/* Patient Name */}
    <Box sx={{ minHeight: { xs: "auto", sm: 56 } }}>
        <Typography variant="subtitle2" color="textSecondary">
            Patient Name
        </Typography>
        <Typography variant="body1">{name}</Typography>
    </Box>

    {/* Preferred Language */}
    <Box sx={{ minHeight: { xs: "auto", sm: 56 } }}>
        <Typography variant="subtitle2" color="textSecondary">
            Preferred Language
        </Typography>
        <Typography variant="body1">{displayLanguage}</Typography>
    </Box>

    {accountId === "2" && (
        <Box sx={{ minHeight: { xs: "auto", sm: 56 } }}>
            <Typography variant="subtitle2" color="textSecondary">
                Treatment Arm
            </Typography>
            <Typography variant="body1">
                {sessionDetails?.treatment_arm === "test"
                    ? "Test"
                    : sessionDetails?.treatment_arm === "control"
                        ? "Control"
                        : "-"}
            </Typography>
        </Box>
    )}

    {accountId === "1" && (
        <Box sx={{ minHeight: { xs: "auto", sm: 56 } }}>
            <Typography variant="subtitle2" color="textSecondary">
                Package Type
            </Typography>
            <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                {sessionDetails?.package_type}
            </Typography>
        </Box>
    )}

    {/* Dynamic fields */}
    {fields.map((field, index) => (
        <Box
            key={index}
            sx={{
                minHeight: { xs: "auto", sm: 56 },
                gridColumn:
                    field.label === "Suggested Care Soundscape" ? "1 / -1" : "auto",
            }}
        >
            <Typography variant="subtitle2" color="textSecondary">
                {field.label}
            </Typography>
            <Typography variant="body1">{field.value}</Typography>
        </Box>
    ))}
</Box>
				</CardContent>
			</Card>


		</FormContainer>
	);
};

export default ViewPin;
